import { STORAGE_REF_TYPE, getBlobDownloadUrl, isStorageRef, uploadBlobVersion } from '../../storage/mnemosyneBlobStorage';

/**
 * Hybrid persist for Plate documents.
 *
 * Small docs stay inline on the aica Mongo row (the historical path). Once the
 * serialized envelope would exceed aica's 10 MB write-boundary (Mongo's real
 * cap is 16 MB; aica rejects earlier with an actionable error), the body is
 * uploaded through the same mnemosyne/S3 pathway sheets and PDFs use and the
 * row only stores a storage pointer. `type` is the shared "body lives on S3"
 * literal — format is `formatType: DOC`.
 */

export const PLATE_JSON_MIME = 'application/json';

/** Matches aica DocumentConstants.MAX_DOCUMENT_CONTENT_BYTES. */
export const MONGO_CONTENT_LIMIT_BYTES = 10 * 1024 * 1024;

export interface PlateStorageRef {
    type: typeof STORAGE_REF_TYPE;
    docId: string;
}

export interface PlateContentEnvelope {
    version: number;
    editor: 'plate';
    value: any[];
}

export const isPlateStorageRef = (value: unknown): value is PlateStorageRef => isStorageRef(value);

function utf8ByteLength(text: string): number {
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text).length;
    // Jest/jsdom (this repo) has no TextEncoder — Buffer is present in the test env.
    if (typeof Buffer !== 'undefined') return Buffer.byteLength(text, 'utf8');
    return unescape(encodeURIComponent(text)).length;
}

function utf8Bytes(text: string): ArrayBuffer {
    if (typeof TextEncoder !== 'undefined') {
        const encoded = new TextEncoder().encode(text);
        return encoded.buffer.slice(encoded.byteOffset, encoded.byteOffset + encoded.byteLength);
    }
    if (typeof Buffer !== 'undefined') {
        const buf = Buffer.from(text, 'utf8');
        return buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength);
    }
    const encoded = unescape(encodeURIComponent(text));
    const bytes = new Uint8Array(encoded.length);
    for (let i = 0; i < encoded.length; i += 1) bytes[i] = encoded.charCodeAt(i);
    return bytes.buffer;
}

export function estimateJsonBytes(value: unknown): number {
    try {
        return utf8ByteLength(JSON.stringify(value));
    } catch {
        return Number.POSITIVE_INFINITY;
    }
}

export function plateContentFitsInMongo(content: unknown): boolean {
    return estimateJsonBytes(content) <= MONGO_CONTENT_LIMIT_BYTES;
}

const jsonBytes = (json: string): ArrayBuffer => utf8Bytes(json);

export interface UploadPlateContentArgs {
    value: any[];
    version: number;
    assesseeOrgId: string;
    investorOrgId?: string;
    existingDocId?: string;
    isDraft?: boolean;
}

export const uploadPlateContentVersion = async ({
    value,
    version,
    assesseeOrgId,
    investorOrgId,
    existingDocId,
    isDraft,
}: UploadPlateContentArgs): Promise<PlateStorageRef> => {
    const envelope: PlateContentEnvelope = { version, editor: 'plate', value };
    const docId = await uploadBlobVersion({
        bytes: jsonBytes(JSON.stringify(envelope)),
        mime: PLATE_JSON_MIME,
        extension: 'json',
        fileNamePrefix: 'plate',
        assesseeOrgId,
        investorOrgId,
        existingDocId,
        isDraft,
    });
    return { type: STORAGE_REF_TYPE, docId };
};

/**
 * Read a stored plate envelope (or a bare node array) back from S3.
 */
export const downloadPlateValue = async (docId: string): Promise<any[]> => {
    const url = await getBlobDownloadUrl(docId);
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to download document content: ${response.status} ${response.statusText}`);
    }
    const parsed = await response.json();
    if (Array.isArray(parsed)) return parsed;
    if (Array.isArray(parsed?.value)) return parsed.value;
    throw new Error('Unsupported document content shape');
};

/**
 * Inline content if it fits in Mongo; otherwise upload to S3 and return a
 * storage pointer (content omitted). `storage: null` on the inline path so a
 * previously-S3 row is cleared when the body shrinks back under the cap
 * (e.g. after first-import image offload).
 */
export const buildPlateSaveFields = async ({
    value,
    version,
    assesseeOrgId,
    investorOrgId,
    existingDocId,
    isDraft,
}: UploadPlateContentArgs): Promise<{ content?: PlateContentEnvelope; storage?: PlateStorageRef | null }> => {
    const envelope: PlateContentEnvelope = { version, editor: 'plate', value };
    if (plateContentFitsInMongo(envelope)) {
        return { content: envelope, storage: null };
    }
    const storage = await uploadPlateContentVersion({
        value,
        version,
        assesseeOrgId,
        investorOrgId,
        existingDocId,
        isDraft,
    });
    return { storage };
};

/**
 * Resolve the plate node array from a document row: prefer inline content,
 * otherwise fetch the S3 body. Empty inline + no storage → [].
 */
export const readPlateValueFromDoc = async (doc: any): Promise<any[]> => {
    const inline = doc?.content?.value ?? doc?.value;
    if (Array.isArray(inline) && inline.length) return inline;
    if (isPlateStorageRef(doc?.storage)) {
        return downloadPlateValue(doc.storage.docId);
    }
    return Array.isArray(inline) ? inline : [];
};
